import { useEffect, useMemo, useState } from "react";
import { api, inr } from "../../lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Building2, Pencil, FileDown, Truck, Receipt, ShoppingBag, Package, ClipboardList, FileSpreadsheet, Wallet, IndianRupee } from "lucide-react";

const PO_STATUSES = ["draft", "sent", "received", "cancelled"];
const PAY_METHODS = ["bank", "upi", "cash", "cheque", "card", "other"];
const PAY_KINDS = ["advance", "part", "final"];
const STATUS_STYLE = {
  draft:     { bg: "#F5F5F5", text: "#3B2F2F", label: "Draft" },
  sent:      { bg: "#DDE7F0", text: "#1F3F5F", label: "Sent" },
  received:  { bg: "#E8EDDF", text: "#2E4A35", label: "Received" },
  cancelled: { bg: "#F5DEC7", text: "#993322", label: "Cancelled" },
};
const PAY_STATUS_STYLE = {
  unpaid:  { bg: "#F5DEC7", text: "#993322", label: "Unpaid" },
  partial: { bg: "#FEF3E2", text: "#8B7355", label: "Partial" },
  paid:    { bg: "#E8EDDF", text: "#2E4A35", label: "Paid" },
};

const emptyLine = () => ({ description: "", hsn: "", qty: 1, unit: "nos", unit_price: 0, tax_pct: 18 });
const emptySupplier = () => ({
  name: "", contact_person: "", email: "", phone: "", gstin: "",
  address: { line1: "", city: "", state: "", pincode: "", country: "India" },
  notes: "",
});
const emptyPo = () => ({
  supplier_id: "", items: [emptyLine()],
  expected_delivery: "", discount: 0, shipping: 0,
  delivery_address_lines: [], notes: "", terms: "", status: "draft",
});
const todayIso = () => new Date().toISOString().slice(0, 10);
const emptyPayment = () => ({ amount: 0, method: "bank", paid_at: todayIso(), reference: "", note: "", kind: "part" });

export default function AdminPurchases() {
  const [suppliers, setSuppliers] = useState([]);
  const [pos, setPos] = useState([]);
  const [tab, setTab] = useState("ledger");
  const [statusFilter, setStatusFilter] = useState("all");

  // Modals
  const [sMode, setSMode] = useState(null);
  const [sEditing, setSEditing] = useState(null);
  const [sForm, setSForm] = useState(emptySupplier());
  const [sSaving, setSSaving] = useState(false);

  const [pMode, setPMode] = useState(null);
  const [pEditing, setPEditing] = useState(null);
  const [pForm, setPForm] = useState(emptyPo());
  const [pSaving, setPSaving] = useState(false);

  const [detail, setDetail] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState(emptyPayment());
  const [paySaving, setPaySaving] = useState(false);

  // Excel export
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const monthAgoIso = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };
  const [exportForm, setExportForm] = useState({ preset: "this-month", start: monthAgoIso(), end: todayIso() });

  const loadSuppliers = () => api.get("/admin/suppliers").then(r => setSuppliers(r.data)).catch(()=>{});
  const loadPos = () => {
    const q = statusFilter !== "all" ? `?status=${statusFilter}` : "";
    api.get(`/admin/purchase-orders${q}`).then(r => setPos(r.data)).catch(()=>{});
  };
  useEffect(() => { loadSuppliers(); }, []);
  useEffect(() => { loadPos(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter]);

  const kpis = useMemo(() => {
    const c = { total: pos.length, draft: 0, sent: 0, received: 0, cancelled: 0, spend: 0, paid: 0, outstanding: 0 };
    pos.forEach(p => {
      c[p.status] = (c[p.status] || 0) + 1;
      if (p.status !== "cancelled") {
        c.spend += Number(p.total || 0);
        c.paid += Number(p.paid || 0);
        c.outstanding += Number(p.outstanding || 0);
      }
    });
    return c;
  }, [pos]);

  // Supplier CRUD
  const openSAdd = () => { setSEditing(null); setSForm(emptySupplier()); setSMode("add"); };
  const openSEdit = (s) => {
    setSEditing(s);
    setSForm({
      name: s.name || "", contact_person: s.contact_person || "",
      email: s.email || "", phone: s.phone || "", gstin: s.gstin || "",
      address: {
        line1: s.address?.line1 || "", city: s.address?.city || "",
        state: s.address?.state || "", pincode: s.address?.pincode || "",
        country: s.address?.country || "India",
      },
      notes: s.notes || "",
    });
    setSMode("edit");
  };
  const closeSModal = () => { setSMode(null); setSEditing(null); setSForm(emptySupplier()); };
  const saveSupplier = async () => {
    if (!sForm.name.trim()) { toast.error("Supplier name is required"); return; }
    setSSaving(true);
    try {
      if (sMode === "add") { await api.post("/admin/suppliers", sForm); toast.success("Supplier added"); }
      else { await api.patch(`/admin/suppliers/${sEditing.supplier_id}`, sForm); toast.success("Supplier updated"); }
      closeSModal(); loadSuppliers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSSaving(false); }
  };
  const deleteSupplier = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}"?`)) return;
    try { await api.delete(`/admin/suppliers/${s.supplier_id}`); toast.success("Supplier deleted"); loadSuppliers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  // PO CRUD
  const openPAdd = () => {
    if (suppliers.length === 0) { toast.error("Add a supplier first"); setTab("suppliers"); return; }
    setPEditing(null);
    setPForm({ ...emptyPo(), supplier_id: suppliers[0].supplier_id });
    setPMode("add");
  };
  const openPEdit = (po) => {
    setPEditing(po);
    setPForm({
      supplier_id: po.supplier?.supplier_id || "",
      items: (po.items || []).map(it => ({
        description: it.description || "", hsn: it.hsn || "",
        qty: it.qty || 1, unit: it.unit || "nos",
        unit_price: it.unit_price || 0, tax_pct: it.tax_pct || 0,
      })),
      expected_delivery: po.expected_delivery || "",
      discount: po.discount || 0, shipping: po.shipping || 0,
      delivery_address_lines: po.delivery_address_lines || [],
      notes: po.notes || "", terms: po.terms || "",
      status: po.status || "draft",
    });
    setPMode("edit");
  };
  const closePModal = () => { setPMode(null); setPEditing(null); setPForm(emptyPo()); };

  const pUpdLine = (i, k, v) => setPForm(f => ({ ...f, items: f.items.map((x, idx) => idx===i ? {...x, [k]: v} : x) }));
  const pAddLine = () => setPForm(f => ({ ...f, items: [...f.items, emptyLine()] }));
  const pRmLine  = (i) => setPForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const pTotals = useMemo(() => {
    let subtotal = 0, tax = 0;
    pForm.items.forEach(it => {
      const line = Number(it.qty || 0) * Number(it.unit_price || 0);
      subtotal += line;
      tax += line * Number(it.tax_pct || 0) / 100;
    });
    return { subtotal, tax, total: subtotal - Number(pForm.discount || 0) + Number(pForm.shipping || 0) + tax };
  }, [pForm]);

  const savePo = async () => {
    if (!pForm.supplier_id) { toast.error("Pick a supplier"); return; }
    if (pForm.items.some(x => !x.description || !x.qty || x.unit_price < 0)) {
      toast.error("Fill description, qty and rate for every line"); return;
    }
    setPSaving(true);
    try {
      if (pMode === "add") { await api.post("/admin/purchase-orders", pForm); toast.success("Purchase order created"); }
      else { await api.patch(`/admin/purchase-orders/${pEditing.po_id}`, pForm); toast.success("Purchase order updated"); }
      closePModal(); loadPos();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setPSaving(false); }
  };

  const setPoStatus = async (po_id, status) => {
    try { await api.post(`/admin/purchase-orders/${po_id}/status`, { status }); toast.success(`Marked as ${status}`); loadPos(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const deletePo = async (po) => {
    if (!window.confirm(`Delete purchase order ${po.po_no}?`)) return;
    try { await api.delete(`/admin/purchase-orders/${po.po_id}`); toast.success("PO deleted"); loadPos(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const downloadPdf = (po) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/purchase-orders/${po.po_id}/pdf`, "_blank");
  };

  // Payments
  const openPayModal = (po) => {
    const outstanding = Number(po.outstanding || 0);
    setPayFor(po);
    setPayForm({ ...emptyPayment(), amount: outstanding > 0 ? Math.round(outstanding) : 0,
                  kind: outstanding <= 0.01 ? "final" : ((po.paid || 0) <= 0.01 ? "advance" : "part") });
  };
  const closePayModal = () => { setPayFor(null); setPayForm(emptyPayment()); };
  const savePayment = async () => {
    if (!(payForm.amount > 0)) { toast.error("Amount must be greater than zero"); return; }
    setPaySaving(true);
    try {
      const r = await api.post(`/admin/purchase-orders/${payFor.po_id}/payments`, payForm);
      toast.success(`Payment of ${inr(payForm.amount)} recorded`);
      closePayModal(); loadPos();
      if (detail && detail.po_id === payFor.po_id) setDetail(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to record payment"); }
    finally { setPaySaving(false); }
  };
  const deletePayment = async (po_id, payment_id) => {
    if (!window.confirm("Delete this payment entry?")) return;
    try {
      const r = await api.delete(`/admin/purchase-orders/${po_id}/payments/${payment_id}`);
      toast.success("Payment removed"); loadPos();
      if (detail && detail.po_id === po_id) setDetail(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  // Excel export
  const downloadStatement = async () => {
    setExporting(true);
    const now = new Date();
    const params = new URLSearchParams();
    let filenameLabel = "custom";
    if (exportForm.preset === "this-month") {
      const m = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      params.append("month", m); filenameLabel = m;
    } else if (exportForm.preset === "last-month") {
      const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      params.append("month", m); filenameLabel = m;
    } else if (exportForm.preset === "all-time") {
      filenameLabel = "all-time";
    } else {
      if (!exportForm.start || !exportForm.end) { toast.error("Pick both start and end dates"); setExporting(false); return; }
      if (exportForm.start > exportForm.end) { toast.error("Start date must be before end date"); setExporting(false); return; }
      params.append("start", exportForm.start); params.append("end", exportForm.end);
      filenameLabel = `${exportForm.start}_to_${exportForm.end}`;
    }
    if (statusFilter !== "all") params.append("status", statusFilter);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/purchase-orders/export.xlsx?${params}`;
    try {
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t.includes("No purchase orders") ? "No purchase orders in this period" : `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `DailyBeans-Purchases-${filenameLabel}.xlsx`;
      a.click();
      toast.success("Excel statement downloaded");
      setExportOpen(false);
    } catch (e) { toast.error(e.message || "Failed to export"); }
    finally { setExporting(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-outfit font-bold flex items-center gap-2">
            <ShoppingBag className="w-7 h-7 text-[#D4AF37]"/> Purchase Ledger
          </h1>
          <div className="text-sm text-stone-500">Record purchases, track advance/final payments, and share branded POs.</div>
        </div>
        <div className="flex items-center gap-2">
          {tab === "ledger" ? (<>
            <button onClick={()=>setExportOpen(true)} data-testid="po-export-excel"
                    className="bg-white border border-[#2E4A35]/40 text-[#2E4A35] rounded-full px-4 py-2.5 text-sm font-medium hover:bg-[#E8EDDF] flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4"/> Export to Excel
            </button>
            <button onClick={openPAdd} data-testid="po-add-btn"
                    className="bg-[#3B2F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-black flex items-center gap-2">
              <Plus className="w-4 h-4"/> Create purchase order
            </button>
          </>) : (
            <button onClick={openSAdd} data-testid="supplier-add-btn"
                    className="bg-[#3B2F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-black flex items-center gap-2">
              <Plus className="w-4 h-4"/> Add supplier
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-1 mb-6 border-b border-stone-200">
        <button onClick={()=>setTab("ledger")} data-testid="tab-ledger"
                className={`px-5 py-2.5 text-sm font-medium border-b-2 transition ${
                  tab === "ledger" ? "border-[#3B2F2F] text-[#3B2F2F]" : "border-transparent text-stone-500 hover:text-stone-800"
                }`}>
          <ClipboardList className="w-4 h-4 inline mr-1.5"/> PO Ledger ({pos.length})
        </button>
        <button onClick={()=>setTab("suppliers")} data-testid="tab-suppliers"
                className={`px-5 py-2.5 text-sm font-medium border-b-2 transition ${
                  tab === "suppliers" ? "border-[#3B2F2F] text-[#3B2F2F]" : "border-transparent text-stone-500 hover:text-stone-800"
                }`}>
          <Building2 className="w-4 h-4 inline mr-1.5"/> Suppliers ({suppliers.length})
        </button>
      </div>

      {tab === "ledger" && (<>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <Kpi label="Total POs" value={kpis.total} accent="#3B2F2F"/>
          <Kpi label="Draft" value={kpis.draft} accent="#8B7355"/>
          <Kpi label="Sent" value={kpis.sent} accent="#1F3F5F"/>
          <Kpi label="Received" value={kpis.received} accent="#2E4A35"/>
          <Kpi label="Paid" value={inr(kpis.paid)} accent="#2E4A35"/>
          <Kpi label="Outstanding" value={inr(kpis.outstanding)} accent="#993322"/>
        </div>

        <div className="flex flex-wrap gap-3 mb-4 bg-white border border-stone-200 rounded-xl p-3 items-center">
          <span className="text-xs uppercase tracking-widest text-stone-500">Status</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="po-status-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {PO_STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_STYLE[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="ml-auto text-xs text-stone-500 flex items-center gap-4">
            <span>Total spend: <span className="font-outfit font-semibold text-stone-800">{inr(kpis.spend)}</span></span>
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>PO #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>GSTIN</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Total</TableHead>
              <TableHead className="w-52">Paid / Outstanding</TableHead>
              <TableHead className="w-36">Status</TableHead>
              <TableHead className="w-48 text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {pos.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-stone-400 py-10">
                  No purchase orders yet.
                </TableCell></TableRow>
              )}
              {pos.map(po => {
                const ps = po.payment_status || "unpaid";
                const paid = Number(po.paid || 0);
                const total = Number(po.total || 0);
                const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
                return (
                <TableRow key={po.po_id} data-testid={`po-row-${po.po_no}`} className="hover:bg-stone-50">
                  <TableCell className="font-medium cursor-pointer" onClick={()=>setDetail(po)}>{po.po_no}</TableCell>
                  <TableCell className="text-xs">{new Date(po.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-xs">
                    <div className="font-medium">{po.supplier?.name || "—"}</div>
                    {po.supplier?.contact_person && <div className="text-stone-400">{po.supplier.contact_person}</div>}
                  </TableCell>
                  <TableCell className="font-mono text-[11px]">
                    {po.supplier?.gstin ? <span className="text-[#2E4A35]">{po.supplier.gstin}</span> : <span className="text-stone-300">—</span>}
                  </TableCell>
                  <TableCell className="text-xs">{(po.items || []).length}</TableCell>
                  <TableCell className="font-outfit font-semibold">{inr(po.total)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge style={{background: PAY_STATUS_STYLE[ps].bg, color: PAY_STATUS_STYLE[ps].text}} data-testid={`po-pay-status-${po.po_no}`}>
                        {PAY_STATUS_STYLE[ps].label}
                      </Badge>
                      <div className="flex-1">
                        <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#2E4A35]" style={{width: `${pct}%`}}/>
                        </div>
                        <div className="text-[10px] text-stone-500 mt-0.5">
                          {inr(paid)} of {inr(total)}
                          {po.outstanding > 0 && <span className="text-[#993322] font-medium"> · {inr(po.outstanding)} due</span>}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Select value={po.status} onValueChange={(v)=>setPoStatus(po.po_id, v)}>
                      <SelectTrigger className="h-8 text-xs"
                        style={{background: STATUS_STYLE[po.status]?.bg, color: STATUS_STYLE[po.status]?.text, borderColor: (STATUS_STYLE[po.status]?.text || "#000")+"40"}}
                        data-testid={`po-status-${po.po_no}`}>
                        <SelectValue/>
                      </SelectTrigger>
                      <SelectContent>
                        {PO_STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_STYLE[s].label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <button onClick={()=>openPayModal(po)} title="Log payment"
                            data-testid={`po-log-payment-${po.po_no}`}
                            disabled={po.status === "cancelled"}
                            className="text-[#2E4A35] hover:bg-[#E8EDDF] rounded-md p-1.5 inline-flex disabled:opacity-30">
                      <Wallet className="w-3.5 h-3.5"/>
                    </button>
                    <button onClick={()=>downloadPdf(po)} title="Download branded PO PDF"
                            data-testid={`po-pdf-${po.po_no}`}
                            className="text-[#D4AF37] hover:bg-[#FEF3E2] rounded-md p-1.5 inline-flex ml-1">
                      <FileDown className="w-3.5 h-3.5"/>
                    </button>
                    <button onClick={()=>openPEdit(po)} title="Edit"
                            data-testid={`po-edit-${po.po_no}`}
                            className="text-[#3B2F2F] hover:bg-stone-100 rounded-md p-1.5 inline-flex ml-1">
                      <Pencil className="w-3.5 h-3.5"/>
                    </button>
                    <button onClick={()=>deletePo(po)} title="Delete"
                            data-testid={`po-delete-${po.po_no}`}
                            className="text-[#993322] hover:bg-red-50 rounded-md p-1.5 inline-flex ml-1">
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  </TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </div>
      </>)}

      {tab === "suppliers" && (
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Supplier</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Phone / Email</TableHead>
              <TableHead>GSTIN</TableHead>
              <TableHead>City</TableHead>
              <TableHead className="w-32 text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {suppliers.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-stone-400 py-10">
                  No suppliers yet.
                </TableCell></TableRow>
              )}
              {suppliers.map(s => (
                <TableRow key={s.supplier_id} data-testid={`supplier-row-${s.name}`} className="hover:bg-stone-50">
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="text-xs">{s.contact_person || "—"}</TableCell>
                  <TableCell className="text-xs">
                    {s.phone && <div className="font-mono">{s.phone}</div>}
                    {s.email && <div className="text-stone-500">{s.email}</div>}
                    {!s.phone && !s.email && <span className="text-stone-300">—</span>}
                  </TableCell>
                  <TableCell className="font-mono text-[11px]">{s.gstin || <span className="text-stone-300">—</span>}</TableCell>
                  <TableCell className="text-xs">{s.address?.city || "—"}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <button onClick={()=>openSEdit(s)} data-testid={`supplier-edit-${s.name}`}
                            className="text-[#3B2F2F] hover:bg-stone-100 rounded-md p-1.5 inline-flex" title="Edit">
                      <Pencil className="w-3.5 h-3.5"/>
                    </button>
                    <button onClick={()=>deleteSupplier(s)} data-testid={`supplier-delete-${s.name}`}
                            className="text-[#993322] hover:bg-red-50 rounded-md p-1.5 inline-flex ml-1" title="Delete">
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* PO detail with payments log */}
      <Dialog open={!!detail} onOpenChange={v=>!v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {detail && (<>
            <DialogHeader><DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5"/> Purchase Order {detail.po_no}
            </DialogTitle></DialogHeader>
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Info label="Supplier">{detail.supplier?.name || "—"}</Info>
                <Info label="Contact">{detail.supplier?.contact_person || "—"}</Info>
                <Info label="Phone">{detail.supplier?.phone || "—"}</Info>
                <Info label="Email">{detail.supplier?.email || "—"}</Info>
                <Info label="GSTIN">{detail.supplier?.gstin || "—"}</Info>
                <Info label="Expected"><Truck className="w-3 h-3 inline mr-1"/>{detail.expected_delivery || "—"}</Info>
              </div>
              <div className="border-t pt-3">
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Items</div>
                <div className="space-y-1.5">
                  {(detail.items || []).map((i,k)=>(
                    <div key={k} className="flex justify-between text-sm">
                      <span>{i.qty}× {i.description} <span className="text-[#8B7355]">· {i.hsn || "—"}</span> <span className="text-stone-400">@ {inr(i.unit_price)} ({i.tax_pct||0}% GST)</span></span>
                      <span className="font-outfit">{inr(i.line_total)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-3 pt-3 border-t font-outfit font-bold text-base">
                  <span>Total</span><span>{inr(detail.total)}</span>
                </div>
              </div>

              {/* Payments Log */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
                    <Wallet className="w-3.5 h-3.5"/> Payments log
                  </div>
                  <button onClick={()=>openPayModal(detail)}
                          data-testid="po-detail-log-payment"
                          className="text-xs text-[#2E4A35] hover:underline flex items-center gap-1">
                    <Plus className="w-3 h-3"/> Add payment
                  </button>
                </div>
                <div className="bg-[#FEF3E2]/40 border border-[#D4AF37]/30 rounded-lg p-3 mb-3">
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div><div className="text-[10px] uppercase tracking-widest text-stone-500">Paid</div>
                      <div className="font-outfit font-bold text-lg text-[#2E4A35]">{inr(detail.paid)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-widest text-stone-500">Outstanding</div>
                      <div className="font-outfit font-bold text-lg text-[#993322]">{inr(detail.outstanding)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-widest text-stone-500">Status</div>
                      <div className="mt-1">
                        <Badge style={{background: PAY_STATUS_STYLE[detail.payment_status||"unpaid"].bg, color: PAY_STATUS_STYLE[detail.payment_status||"unpaid"].text}}>
                          {PAY_STATUS_STYLE[detail.payment_status||"unpaid"].label}
                        </Badge>
                      </div></div>
                  </div>
                </div>
                {(detail.payments || []).length === 0 ? (
                  <div className="text-xs text-stone-400 italic">No payments recorded yet. Click "Add payment" to log an advance or final payment.</div>
                ) : (
                  <div className="space-y-1.5">
                    {(detail.payments || []).map(p => (
                      <div key={p.payment_id} className="flex justify-between items-center bg-stone-50 rounded-md px-3 py-2 text-xs">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-outfit font-semibold text-stone-800">{inr(p.amount)}</span>
                            <span className="uppercase text-[9px] tracking-widest text-[#8B7355]">{p.kind}</span>
                            <span className="text-stone-500">via {p.method}</span>
                            <span className="text-stone-400">· {p.paid_at}</span>
                          </div>
                          {(p.reference || p.note) && (
                            <div className="text-stone-500 text-[11px] mt-0.5">
                              {p.reference && <span className="font-mono">ref: {p.reference}</span>}
                              {p.reference && p.note && <span> · </span>}
                              {p.note}
                            </div>
                          )}
                        </div>
                        <button onClick={()=>deletePayment(detail.po_id, p.payment_id)}
                                data-testid={`po-payment-delete-${p.payment_id}`}
                                className="text-[#993322] hover:bg-red-50 rounded-md p-1 ml-2">
                          <Trash2 className="w-3 h-3"/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {detail.notes && (
                <div className="border-t pt-3">
                  <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Notes</div>
                  <div className="text-sm text-stone-700 whitespace-pre-wrap">{detail.notes}</div>
                </div>
              )}
              <div className="flex gap-2 justify-end pt-3 border-t">
                <button onClick={()=>downloadPdf(detail)}
                        className="border border-[#D4AF37] text-[#8B7355] rounded-full px-5 py-2 text-sm font-medium hover:bg-[#FEF3E2] flex items-center gap-1.5">
                  <FileDown className="w-3.5 h-3.5"/> Download PDF
                </button>
                <button onClick={()=>{ setDetail(null); openPEdit(detail); }}
                        className="bg-[#3B2F2F] text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-black flex items-center gap-1.5">
                  <Pencil className="w-3.5 h-3.5"/> Edit PO
                </button>
              </div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Payment modal */}
      <Dialog open={!!payFor} onOpenChange={v=>!v && closePayModal()}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#2E4A35]"/> Log payment · {payFor?.po_no}
          </DialogTitle></DialogHeader>
          {payFor && (
            <div className="space-y-4">
              <div className="bg-[#FEF3E2]/50 border border-[#D4AF37]/30 rounded-lg p-3 text-xs grid grid-cols-3 gap-2">
                <div><div className="text-[10px] uppercase text-stone-500">Total</div><div className="font-outfit font-semibold text-stone-800">{inr(payFor.total)}</div></div>
                <div><div className="text-[10px] uppercase text-stone-500">Paid</div><div className="font-outfit font-semibold text-[#2E4A35]">{inr(payFor.paid || 0)}</div></div>
                <div><div className="text-[10px] uppercase text-stone-500">Outstanding</div><div className="font-outfit font-semibold text-[#993322]">{inr(payFor.outstanding || 0)}</div></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1"><IndianRupee className="w-3 h-3"/>Amount</label>
                  <Input type="number" min="0" step="0.01" value={payForm.amount}
                          onChange={e=>setPayForm(f=>({...f, amount: Number(e.target.value)}))}
                          data-testid="pay-amount"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500">Type</label>
                  <Select value={payForm.kind} onValueChange={v=>setPayForm(f=>({...f, kind: v}))}>
                    <SelectTrigger data-testid="pay-kind"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {PAY_KINDS.map(k => <SelectItem key={k} value={k}>{k.charAt(0).toUpperCase()+k.slice(1)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500">Method</label>
                  <Select value={payForm.method} onValueChange={v=>setPayForm(f=>({...f, method: v}))}>
                    <SelectTrigger data-testid="pay-method"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {PAY_METHODS.map(m => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500">Paid on</label>
                  <Input type="date" value={payForm.paid_at}
                          onChange={e=>setPayForm(f=>({...f, paid_at: e.target.value}))}
                          data-testid="pay-date"/>
                </div>
                <Input className="col-span-2" placeholder="Reference (UTR / cheque # / txn id)"
                        value={payForm.reference} onChange={e=>setPayForm(f=>({...f, reference: e.target.value}))}
                        data-testid="pay-reference"/>
                <Input className="col-span-2" placeholder="Note (optional)"
                        value={payForm.note} onChange={e=>setPayForm(f=>({...f, note: e.target.value}))}/>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button onClick={closePayModal} className="border border-stone-300 rounded-full px-5 py-2 text-sm hover:bg-stone-50">Cancel</button>
                <button onClick={savePayment} disabled={paySaving} data-testid="pay-save"
                        className="bg-[#2E4A35] text-white rounded-full px-6 py-2 text-sm font-medium hover:bg-[#25392a] disabled:opacity-60 flex items-center gap-2">
                  <Wallet className="w-4 h-4"/>
                  {paySaving ? "Saving…" : `Record ${inr(payForm.amount || 0)}`}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Export modal */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#2E4A35]"/> Export purchase orders to Excel
          </DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-600">
              Download a bank-statement style spreadsheet of purchase orders for the period you pick.
              Includes supplier details, items, GST, and paid vs outstanding amounts.
            </p>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Period</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: "this-month", label: "This month" },
                  { key: "last-month", label: "Last month" },
                  { key: "last-30",    label: "Last 30 days" },
                  { key: "custom",     label: "Custom range" },
                  { key: "all-time",   label: "All time" },
                ].map(opt => (
                  <button key={opt.key}
                    data-testid={`po-export-preset-${opt.key}`}
                    onClick={()=>{
                      const now = new Date();
                      if (opt.key === "last-30") {
                        const d = new Date(); d.setDate(d.getDate() - 30);
                        setExportForm({ preset: "custom", start: d.toISOString().slice(0,10), end: todayIso() });
                      } else if (opt.key === "custom") {
                        setExportForm(f => ({ ...f, preset: "custom" }));
                      } else if (opt.key === "this-month") {
                        const s = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0,10);
                        setExportForm({ preset: "this-month", start: s, end: todayIso() });
                      } else if (opt.key === "last-month") {
                        const s = new Date(now.getFullYear(), now.getMonth()-1, 1);
                        const e = new Date(now.getFullYear(), now.getMonth(), 0);
                        setExportForm({ preset: "last-month", start: s.toISOString().slice(0,10), end: e.toISOString().slice(0,10) });
                      } else {
                        setExportForm({ preset: "all-time", start: "", end: "" });
                      }
                    }}
                    className={`text-sm px-3 py-2 rounded-lg border transition ${
                      exportForm.preset === opt.key
                        ? "bg-[#3B2F2F] text-white border-[#3B2F2F]"
                        : "bg-white border-stone-200 hover:border-[#D4AF37] text-stone-700"
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {exportForm.preset === "custom" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500">From</label>
                  <Input type="date" value={exportForm.start}
                          onChange={e=>setExportForm(f=>({...f, start: e.target.value}))}
                          data-testid="po-export-start"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500">To</label>
                  <Input type="date" value={exportForm.end}
                          onChange={e=>setExportForm(f=>({...f, end: e.target.value}))}
                          data-testid="po-export-end"/>
                </div>
              </div>
            )}
            {statusFilter !== "all" && (
              <div className="bg-[#FEF3E2]/50 border border-[#D4AF37]/30 rounded-lg p-3 text-xs text-stone-700">
                <div className="font-semibold text-[#8B7355] uppercase tracking-wide mb-1">Active filter</div>
                <div>Status: <span className="font-medium">{statusFilter}</span></div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <button onClick={()=>setExportOpen(false)}
                      className="border border-stone-300 rounded-full px-5 py-2 text-sm hover:bg-stone-50">Cancel</button>
              <button onClick={downloadStatement} disabled={exporting}
                      data-testid="po-export-download"
                      className="bg-[#2E4A35] text-white rounded-full px-6 py-2 text-sm font-medium hover:bg-[#25392a] disabled:opacity-60 flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4"/>
                {exporting ? "Preparing…" : "Download Excel"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Supplier modal */}
      <Dialog open={!!sMode} onOpenChange={v=>!v && closeSModal()}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>
            {sMode === "edit" ? `Edit supplier · ${sEditing?.name}` : "Add supplier"}
          </DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Supplier / Company name *" value={sForm.name} onChange={e=>setSForm(f=>({...f, name: e.target.value}))} data-testid="supplier-name"/>
              <Input placeholder="Contact person" value={sForm.contact_person} onChange={e=>setSForm(f=>({...f, contact_person: e.target.value}))}/>
              <Input placeholder="Phone" value={sForm.phone} onChange={e=>setSForm(f=>({...f, phone: e.target.value}))} data-testid="supplier-phone"/>
              <Input placeholder="Email" value={sForm.email} onChange={e=>setSForm(f=>({...f, email: e.target.value}))} data-testid="supplier-email"/>
              <Input className="col-span-2" placeholder="GSTIN (e.g. 29AAAAA0000A1Z5)" value={sForm.gstin} onChange={e=>setSForm(f=>({...f, gstin: (e.target.value||"").toUpperCase()}))} data-testid="supplier-gstin"/>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Address</div>
              <div className="grid grid-cols-2 gap-3">
                <Input className="col-span-2" placeholder="Street address" value={sForm.address.line1} onChange={e=>setSForm(f=>({...f, address: {...f.address, line1: e.target.value}}))}/>
                <Input placeholder="City" value={sForm.address.city} onChange={e=>setSForm(f=>({...f, address: {...f.address, city: e.target.value}}))}/>
                <Input placeholder="State" value={sForm.address.state} onChange={e=>setSForm(f=>({...f, address: {...f.address, state: e.target.value}}))}/>
                <Input placeholder="Pincode" value={sForm.address.pincode} onChange={e=>setSForm(f=>({...f, address: {...f.address, pincode: e.target.value}}))}/>
                <Input placeholder="Country" value={sForm.address.country} onChange={e=>setSForm(f=>({...f, address: {...f.address, country: e.target.value}}))}/>
              </div>
            </div>
            <Input placeholder="Notes (payment terms, credit period, etc.)" value={sForm.notes} onChange={e=>setSForm(f=>({...f, notes: e.target.value}))}/>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <button onClick={closeSModal} className="border border-stone-300 rounded-full px-5 py-2 text-sm hover:bg-stone-50">Cancel</button>
              <button onClick={saveSupplier} disabled={sSaving} data-testid="supplier-save"
                      className="bg-[#3B2F2F] text-white rounded-full px-6 py-2 text-sm font-medium hover:bg-black disabled:opacity-60">
                {sSaving ? "Saving…" : (sMode === "edit" ? "Save changes" : "Add supplier")}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PO modal */}
      <Dialog open={!!pMode} onOpenChange={v=>!v && closePModal()}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5"/> {pMode === "edit" ? `Edit PO · ${pEditing?.po_no}` : "Create purchase order"}
          </DialogTitle></DialogHeader>
          <div className="space-y-5">
            <section className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-stone-500">Supplier *</label>
                <Select value={pForm.supplier_id} onValueChange={v=>setPForm(f=>({...f, supplier_id: v}))}>
                  <SelectTrigger data-testid="po-supplier-select"><SelectValue placeholder="Pick a supplier"/></SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}{s.gstin ? ` · ${s.gstin}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500">Expected delivery</label>
                <Input type="date" value={pForm.expected_delivery} onChange={e=>setPForm(f=>({...f, expected_delivery: e.target.value}))} data-testid="po-expected-delivery"/>
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-stone-500">Line items</div>
                <button onClick={pAddLine} className="text-xs text-[#3B2F2F] hover:underline flex items-center gap-1" data-testid="po-add-line"><Plus className="w-3 h-3"/> Add row</button>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-stone-500 px-1">
                  <div className="col-span-4">Description</div>
                  <div className="col-span-2">HSN/SAC</div>
                  <div className="col-span-1">Qty</div>
                  <div className="col-span-1">Wt</div>
                  <div className="col-span-2">Rate ₹</div>
                  <div className="col-span-1">GST %</div>
                  <div className="col-span-1"></div>
                </div>
                {pForm.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-4" placeholder="Product / service description" value={it.description} onChange={e=>pUpdLine(i, "description", e.target.value)} data-testid={`po-line-${i}-desc`}/>
                    <Input className="col-span-2" placeholder="HSN" value={it.hsn} onChange={e=>pUpdLine(i, "hsn", e.target.value)}/>
                    <Input className="col-span-1" type="number" min="0.01" step="0.01" value={it.qty} onChange={e=>pUpdLine(i, "qty", Number(e.target.value))} data-testid={`po-line-${i}-qty`}/>
                    <Input className="col-span-1" placeholder="nos" value={it.unit} onChange={e=>pUpdLine(i, "unit", e.target.value)}/>
                    <Input className="col-span-2" type="number" min="0" step="0.01" value={it.unit_price} onChange={e=>pUpdLine(i, "unit_price", Number(e.target.value))} data-testid={`po-line-${i}-rate`}/>
                    <Input className="col-span-1" type="number" min="0" max="100" step="0.01" value={it.tax_pct} onChange={e=>pUpdLine(i, "tax_pct", Number(e.target.value))}/>
                    <button onClick={()=>pRmLine(i)} disabled={pForm.items.length===1} className="col-span-1 text-red-500 disabled:text-stone-300 flex justify-center"><Trash2 className="w-4 h-4"/></button>
                  </div>
                ))}
              </div>
            </section>

            <section className="grid grid-cols-4 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500">Discount ₹</label>
                <Input type="number" min="0" value={pForm.discount} onChange={e=>setPForm(f=>({...f, discount: Number(e.target.value)}))}/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500">Shipping ₹</label>
                <Input type="number" min="0" value={pForm.shipping} onChange={e=>setPForm(f=>({...f, shipping: Number(e.target.value)}))}/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500">Status</label>
                <Select value={pForm.status} onValueChange={v=>setPForm(f=>({...f, status: v}))}>
                  <SelectTrigger data-testid="po-status-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {PO_STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_STYLE[s].label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-[#FEF3E2]/50 border border-[#D4AF37]/30 rounded-lg p-2">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Grand total</div>
                <div className="font-outfit font-bold text-xl text-[#3B2F2F]" data-testid="po-grand-total">{inr(pTotals.total)}</div>
                <div className="text-[10px] text-stone-500">Subtotal {inr(pTotals.subtotal)} + GST {inr(pTotals.tax)}</div>
              </div>
            </section>

            <section className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500">Notes to supplier</label>
                <textarea rows={3} value={pForm.notes} onChange={e=>setPForm(f=>({...f, notes: e.target.value}))}
                          className="w-full border border-stone-200 rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                          placeholder="e.g. Deliver between 10am–5pm on weekdays"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500">Terms & conditions (optional override)</label>
                <textarea rows={3} value={pForm.terms} onChange={e=>setPForm(f=>({...f, terms: e.target.value}))}
                          className="w-full border border-stone-200 rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                          placeholder="Leave blank to use the default terms."/>
              </div>
            </section>

            <div className="flex justify-end gap-2 pt-3 border-t">
              <button onClick={closePModal} className="border border-stone-300 rounded-full px-5 py-2 text-sm hover:bg-stone-50">Cancel</button>
              <button onClick={savePo} disabled={pSaving} data-testid="po-save"
                      className="bg-[#3B2F2F] text-white rounded-full px-6 py-2 text-sm font-medium hover:bg-black disabled:opacity-60">
                {pSaving ? "Saving…" : (pMode === "edit" ? "Save changes" : "Create purchase order")}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4">
      <div className="text-xs uppercase tracking-widest text-stone-500">{label}</div>
      <div className="font-outfit font-bold text-xl mt-1" style={{color: accent}}>{value}</div>
    </div>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className="text-sm text-stone-800 mt-0.5">{children}</div>
    </div>
  );
}
